import { useI18n } from '@cascade/react/i18n';
import { useAuthentication } from '@cascade/react/authentication';
import { navigateExternal } from '@cascade/react-dom/utils';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EmailForm } from './email-form/index.ts';
import { EmailNotFound } from './email-not-found/index.ts';
import { MagicLinkSent } from './magic-link-sent/index.ts';
import { TermsDeclined } from './terms-declined/index.ts';
import { getTitleKey, hasAuthReason } from './SignIn.helpers.ts';
import type { TSignInLocationState } from './SignIn.types.ts';
import { Layout } from '#layout/index.ts';

export const SignIn = () => {
  const { translate, translateError } = useI18n();
  const { magicAuth, error, signIn } = useAuthentication();

  const location = useLocation();
  const navigate = useNavigate();
  const [dismissedError, setDismissedError] = useState<unknown>();

  const locationState = location.state as TSignInLocationState | null;
  const isTermsDeclined = Boolean(locationState?.termsDeclined);
  const activeError = error === dismissedError ? undefined : error;
  const isEmailNotFound = hasAuthReason(activeError, 'unsupported_domain');
  const isEmailCheck = hasAuthReason(activeError, 'missing_email');
  const isProviderRefused = hasAuthReason(activeError, 'unsupported_provider');
  const showForm = !isTermsDeclined && !magicAuth && !isEmailNotFound;
  const showError = Boolean(activeError) && !isEmailCheck && !isProviderRefused;

  const titleKey = getTitleKey(isTermsDeclined, isEmailCheck);
  const title = translate(titleKey);
  const subtitle = isEmailCheck
    ? translate('signIn.emailCheck.subtitle')
    : undefined;

  const requestSignIn = async (email: string) => {
    const result = await signIn(email);

    if (result.kind === 'redirect') {
      navigateExternal(result.url);
    }
  };

  const dismissError = () => setDismissedError(error);

  const dismissTermsDeclined = () =>
    navigate(
      { pathname: location.pathname, search: location.search },
      {
        replace: true,
      },
    );

  return (
    <Layout mode='card' title={title} subtitle={subtitle}>
      {isTermsDeclined ? <TermsDeclined onBack={dismissTermsDeclined} /> : null}
      {magicAuth ? (
        <MagicLinkSent
          email={magicAuth.email}
          canResendAt={magicAuth.canResendAt}
          onResend={() => requestSignIn(magicAuth.email)}
        />
      ) : null}
      {isEmailNotFound ? <EmailNotFound onBack={dismissError} /> : null}
      {showForm ? (
        <EmailForm
          errorMessage={showError ? translateError(activeError) : undefined}
          onSubmit={requestSignIn}
        />
      ) : null}
    </Layout>
  );
};
